import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Shield, CheckCircle, ArrowLeft, ExternalLink, Trash2, Image as ImageIcon } from 'lucide-react';
import { authAPI, memberAPI } from '../services/api';
import type { WireGuardConfig } from '../types';

export default function SetupPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<WireGuardConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    try {
      const authCheck = await authAPI.checkMemberAuth();
      if (!authCheck.authenticated) {
        navigate('/');
        return;
      }

      const configData = await memberAPI.getWireGuardConfig();
      setConfig(configData);
    } catch (err: any) {
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }

  // Download-state bookkeeping so we can show "前回 X時間前にダウンロード" notes.
  // Persisted in localStorage keyed by deviceGuid (so two members on same browser
  // don't see each other's state). The browser cannot actually see the Downloads
  // folder — this only remembers clicks on our button.
  const [qrDownloadedAt, setQrDownloadedAt] = useState<Date | null>(null);
  const [confDownloadedAt, setConfDownloadedAt] = useState<Date | null>(null);

  // Page-session-stable .conf filename. Computed ONCE at component mount and
  // used both in the on-screen instructions AND as the actual filename in
  // handleDownload — so what the user reads on the page matches exactly the
  // file that lands in their Downloads folder, regardless of how long they
  // linger before tapping. Format `sas_HHMMSS.conf` keeps the auto-derived
  // WireGuard tunnel name within the 15-char limit.
  const [confFilename] = useState<string>(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `sas_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.conf`;
  });

  const qrStorageKey = config?.deviceGuid ? `sas-dl-qr-${config.deviceGuid}` : '';
  const confStorageKey = config?.deviceGuid ? `sas-dl-conf-${config.deviceGuid}` : '';

  useEffect(() => {
    if (!config?.deviceGuid) return;
    const qrTs = localStorage.getItem(`sas-dl-qr-${config.deviceGuid}`);
    if (qrTs) setQrDownloadedAt(new Date(qrTs));
    const confTs = localStorage.getItem(`sas-dl-conf-${config.deviceGuid}`);
    if (confTs) setConfDownloadedAt(new Date(confTs));
  }, [config?.deviceGuid]);

  function formatRelative(d: Date): string {
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'たった今';
    if (m < 60) return `${m}分前`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}時間前`;
    const days = Math.floor(h / 24);
    return `${days}日前`;
  }

  // QR image download — primary path for single-phone Android setup.
  // WireGuard reads the config from the QR pixels, NOT the filename, so Chrome's
  // "(1)" dedupe has no functional impact (user can import the same config from
  // any of SAS-QR.png / SAS-QR (1).png / SAS-QR (2).png identically).
  function handleQrDownload() {
    if (!config?.qrCode) return;
    const a = document.createElement('a');
    a.href = config.qrCode;
    a.download = 'SAS-QR.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    const now = new Date();
    setQrDownloadedAt(now);
    if (qrStorageKey) localStorage.setItem(qrStorageKey, now.toISOString());
  }

  // .conf download — primary path. The filename comes from `confFilename`,
  // which was computed once when the page loaded, so the instructions and the
  // saved file always match.
  function handleDownload() {
    if (!config) return;
    const blob = new Blob([config.configText], { type: 'application/x-wireguard-profile' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = confFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    const now = new Date();
    setConfDownloadedAt(now);
    if (confStorageKey) localStorage.setItem(confStorageKey, now.toISOString());
  }


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            ダッシュボードに戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">セットアップガイド</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Setup Instructions */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">セットアップ手順</h3>
          <div className="space-y-6">

            {/* Step 1: Install WireGuard */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-2">WireGuardアプリをインストール</h4>
                <p className="text-gray-600 mb-3">
                  {isIOS && 'App Store'}
                  {isAndroid && 'Google Play Store'}
                  {!isIOS && !isAndroid && 'App StoreまたはGoogle Play Store'}
                  からWireGuardアプリをダウンロードしてください
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  {isIOS && (
                    <a
                      href="https://apps.apple.com/jp/app/wireguard/id1441195209"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors text-sm font-semibold"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>App Storeで開く</span>
                    </a>
                  )}
                  {isAndroid && (
                    <a
                      href="https://play.google.com/store/apps/details?id=com.wireguard.android"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-semibold"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Google Playで開く</span>
                    </a>
                  )}
                  {!isIOS && !isAndroid && (
                    <>
                      <a
                        href="https://apps.apple.com/jp/app/wireguard/id1441195209"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors text-sm font-semibold"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>App Store (iOS)</span>
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.wireguard.android"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-semibold"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Google Play (Android)</span>
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Import VPN Config */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-3">VPN設定をインポート</h4>

                {/* Primary: .conf file download. Confirmed in 2026-05-08 customer
                    testing that some Android vendors' WireGuard build (e.g. Motorola)
                    rejects PNG files passed through the "Import from file/archive"
                    picker — it parses the byte stream as .conf text and errors. The
                    direct .conf download path works on every Android and iOS. */}
                <button
                  onClick={handleDownload}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors shadow-md text-base"
                >
                  <Download className="w-6 h-6" />
                  <span>設定ファイル (.conf) をダウンロード（推奨）</span>
                </button>
                <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                  最も確実な方法です。すべての Android／iOS の WireGuard で動作します。
                </p>
                {confDownloadedAt && (
                  <div className="mt-2 bg-blue-50 border-l-4 border-blue-300 rounded-r p-2 text-xs text-blue-900">
                    ℹ 前回 {confDownloadedAt.toLocaleString('ja-JP')} にダウンロード済み（{formatRelative(confDownloadedAt)}）。
                    まだ WireGuard に取り込んでいない場合はもう一度タップしてください。
                  </div>
                )}

                {/* .conf import instructions — shown unconditionally so first-time
                    users see the steps before tapping. */}
                <div className="mt-3 bg-green-50 border-l-4 border-green-400 rounded-r-lg p-4">
                  <p className="font-semibold text-green-900 mb-2 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    次の手順で WireGuard に取り込んでください
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-green-900 mb-3">
                    <li><strong>WireGuardアプリ</strong>を起動</li>
                    <li>右下の <strong>「＋」ボタン</strong> → <strong>「ファイル、アーカイブからインポート」</strong>（または <strong>「Import from file or archive」</strong>）</li>
                    <li>ダウンロードフォルダから <code className="bg-green-100 px-1 rounded">{confFilename}</code> を選択</li>
                    <li>トンネル名に <code className="bg-green-100 px-1 rounded">{confFilename.replace(/\.conf$/, '')}</code> が自動で入ります → 右上の <strong>保存</strong>（💾）をタップ</li>
                  </ol>
                  <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-900">
                    💡 iOS の場合は WireGuard を起動 → 「＋」→「ファイルからインポート」→ ダウンロードフォルダから <code className="bg-yellow-200 px-1 rounded">.conf</code> ファイルを選択してください。
                  </div>
                </div>

                {/* Secondary: QR image download, collapsed by default */}
                <details className="mt-4 pt-4 border-t border-gray-200">
                  <summary className="text-xs font-semibold text-gray-500 cursor-pointer hover:text-gray-700">
                    別の方法: QRコード画像をダウンロード
                  </summary>
                  <div className="mt-3">
                    <button
                      onClick={handleQrDownload}
                      className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white hover:bg-gray-50 text-blue-700 border-2 border-blue-500 rounded-lg font-semibold transition-colors"
                    >
                      <ImageIcon className="w-5 h-5" />
                      <span>QRコード画像をダウンロード (.png)</span>
                    </button>
                    <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                      画像として保存した QR を WireGuard に読み込ませる方法です。一部の機種では画像インポートに対応していない場合があります。
                    </p>
                    {qrDownloadedAt && (
                      <div className="mt-2 bg-blue-50 border-l-4 border-blue-300 rounded-r p-2 text-xs text-blue-900">
                        ℹ 前回 {qrDownloadedAt.toLocaleString('ja-JP')} にダウンロード済み（{formatRelative(qrDownloadedAt)}）。
                      </div>
                    )}

                    {/* QR-import instructions, shown after the user taps download */}
                    {qrDownloadedAt && (
                      <div className="mt-3 bg-green-50 border-l-4 border-green-400 rounded-r-lg p-4">
                        <p className="font-semibold text-green-900 mb-2 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          次の手順で WireGuard に取り込んでください
                        </p>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-green-900 mb-3">
                          <li><strong>WireGuardアプリ</strong>を起動</li>
                          <li>右下の <strong>「＋」ボタン</strong> → <strong>「QRコードをスキャン」</strong>（または <strong>「Scan from QR code」</strong>）</li>
                          <li>カメラが起動したら画面下の <strong>「ギャラリーから選択」</strong> または <strong>画像アイコン</strong> をタップ</li>
                          <li>ダウンロードフォルダ（またはギャラリー）から <code className="bg-green-100 px-1 rounded">SAS-QR.png</code> を選択</li>
                          <li>QRが自動で読み取られ、トンネル情報が表示されます</li>
                          <li>「Name / 名前」に短い名前（例: <code className="bg-green-100 px-1 rounded">sas</code>）を入力 → 右上の <strong>保存</strong>（💾）をタップ</li>
                        </ol>
                        <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-900">
                          💡 何度ダウンロードしても大丈夫です。QRコードの中身は毎回同じなので、<code className="bg-yellow-200 px-1 rounded">SAS-QR (1).png</code> のようなファイル名になっても問題ありません。古い画像は削除して構いません。
                        </div>
                      </div>
                    )}
                  </div>
                </details>

                {/* Tertiary: QR Code display, for 2-device setup */}
                <details className="mt-4 pt-4 border-t border-gray-200">
                  <summary className="text-xs font-semibold text-gray-500 cursor-pointer hover:text-gray-700">
                    別の端末から読み込む場合（QRコードを表示）
                  </summary>
                  <div className="mt-3 text-center bg-gray-50 rounded-lg p-4">
                    <div className="inline-block p-3 bg-white rounded-lg shadow-sm">
                      <img
                        src={config?.qrCode}
                        alt="WireGuard QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                    <p className="mt-3 text-xs text-gray-600">
                      別のスマートフォンで WireGuard アプリを開き、「＋」→「QRコードをスキャン」→ この画面をカメラで撮影してください。
                    </p>
                  </div>
                </details>

                {/* VPN Info */}
                <div className="mt-4 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">VPN情報</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">デバイスID:</span>
                      <span className="font-mono text-gray-700">{config?.deviceGuid}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">VPN IP:</span>
                      <span className="font-mono text-gray-700">{config?.vpnIp}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Install CA Certificate */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-2">SAS証明書をインストール</h4>
                <p className="text-gray-600 mb-3 text-sm">
                  広告トラッキングに必要な証明書です
                </p>
                <a
                  href="/certs/sas-ca-cert.cer"
                  className="inline-flex items-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-semibold"
                  {...(!isIOS && { download: true })}
                >
                  <Shield className="w-5 h-5" />
                  <span>証明書をダウンロード (.cer)</span>
                </a>

                <div className="mt-4 space-y-3">
                  {isIOS && (
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <p className="font-semibold text-blue-900 mb-2 text-sm">📱 iOS インストール手順</p>
                      <ol className="list-decimal list-inside space-y-1.5 text-sm text-blue-800">
                        <li>上のボタンから証明書をダウンロード</li>
                        <li>「許可」をタップ</li>
                        <li>設定アプリを開く</li>
                        <li>上部の「プロファイルがダウンロードされました」をタップ</li>
                        <li>「インストール」をタップ（パスコード入力）</li>
                        <li>警告画面で再度「インストール」をタップ</li>
                        <li className="font-bold text-blue-900">設定 → 一般 → 情報 → 証明書信頼設定</li>
                        <li className="font-bold text-blue-900">「sas-net」をONにする（重要！）</li>
                      </ol>
                      <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-900">
                        ⚠️ 証明書名は「sas-net」と表示されます。必ず信頼設定を有効にしてください
                      </div>
                    </div>
                  )}
                  {isAndroid && (
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                      <p className="font-semibold text-green-900 mb-2 text-sm">🤖 Android インストール手順</p>
                      <ol className="list-decimal list-inside space-y-1.5 text-sm text-green-800">
                        <li>設定アプリを開く</li>
                        <li>「セキュリティ」または「セキュリティと現在地情報」</li>
                        <li>「暗号化と認証情報」または「証明書をインストール」</li>
                        <li>「CA証明書」を選択（重要：VPN証明書ではありません）</li>
                        <li>警告で「とにかくインストール」をタップ</li>
                        <li>ダウンロードフォルダから .cer ファイルを選択</li>
                      </ol>
                      <div className="mt-3 p-2 bg-green-100 rounded text-xs text-green-900">
                        ⚠️ 証明書名は「sas-net」と表示されます。インストール後、通知バーに「ネットワークが監視される可能性があります」と表示されますが、これは正常です
                      </div>
                    </div>
                  )}
                  {!isIOS && !isAndroid && (
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <p className="text-gray-700 mb-1">
                        <strong>iOS:</strong> 設定 → プロファイルをインストール → 証明書信頼設定で「sas-net」をON
                      </p>
                      <p className="text-gray-700">
                        <strong>Android:</strong> 設定 → セキュリティ → CA証明書をインストール
                      </p>
                    </div>
                  )}
                  <a href="/check"
                    className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-teal-300 bg-teal-50 px-4 py-3 text-sm text-teal-900 hover:bg-teal-100">
                    <span><strong>設定できたか確認する</strong>：VPNと証明書が正しく設定されているかをチェックします</span>
                    <span aria-hidden>→</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Step 4: Native Apps - Different for Android vs iOS */}
            <div className="flex gap-4">
              <div className={`flex-shrink-0 w-8 h-8 ${isIOS ? 'bg-red-500' : 'bg-green-500'} text-white rounded-full flex items-center justify-center font-bold`}>
                4
              </div>
              <div className="flex-1">
                {isIOS ? (
                  <>
                    <h4 className="font-semibold text-gray-800 mb-2">ネイティブアプリをアンインストール</h4>
                    <p className="text-gray-600 mb-3 text-sm">
                      Safariで広告をトラッキングするため、以下のアプリはアンインストールしてください
                    </p>
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-semibold text-red-900 mb-2 text-sm">アンインストールが必要なアプリ:</p>
                          <ul className="space-y-1 text-sm text-red-800">
                            <li>• YouTube アプリ</li>
                            <li>• Facebook アプリ</li>
                            <li>• Instagram アプリ</li>
                            <li>• X アプリ</li>
                          </ul>
                          <p className="mt-3 text-xs text-red-700 leading-relaxed">
                            <strong>理由:</strong> iOSではネイティブアプリがインストールされていると、リンクをタップした時にブラウザではなくアプリで開いてしまいます。
                            アプリ内では広告トラッキングができないため、Safariブラウザで利用してください。
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                      💡 アンインストール後は、Safariブラウザで直接アクセスしてください
                      （例: youtube.com、instagram.com など）
                    </div>
                  </>
                ) : (
                  <>
                    <h4 className="font-semibold text-gray-800 mb-2">SNS Launcher アプリを使用</h4>
                    <p className="text-gray-600 mb-3 text-sm">
                      Androidでは「SNS Launcher」アプリを使ってSNSにアクセスしてください
                    </p>
                    <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-semibold text-green-900 mb-2 text-sm">SNS Launcherの利点:</p>
                          <ul className="space-y-1 text-sm text-green-800">
                            <li>• YouTube、Facebook、Instagram、Xにワンタップでアクセス</li>
                            <li>• 自動的にChromeブラウザで開きます</li>
                            <li>• ネイティブアプリのアンインストールは不要！</li>
                          </ul>
                          <p className="mt-3 text-xs text-green-700 leading-relaxed">
                            <strong>仕組み:</strong> SNS Launcherは自動的にChromeブラウザを使用してSNSを開きます。
                            YouTube等のネイティブアプリがインストールされていても、Chromeで開くため広告トラッキングが正常に動作します。
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                      💡 SNS LauncherはGoogle Playストアまたは提供されたAPKからインストールしてください
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Step 5: Connect VPN */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                5
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-2">VPNを接続して開始</h4>
                <p className="text-gray-600 text-sm mb-3">
                  WireGuardアプリでVPNをONにして、{isIOS ? 'Safari' : 'Chrome'}でブラウジングを開始してください
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-900 font-semibold mb-2">✅ セットアップ完了後の確認項目:</p>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>• WireGuard VPNが「接続中」になっている</li>
                    <li>• 証明書「sas-net」がインストール済み</li>
                    {isIOS ? (
                      <>
                        <li>• YouTube/Facebook/Instagram/X アプリがアンインストール済み</li>
                        <li>• Safariブラウザで問題なくWebサイトが表示される</li>
                      </>
                    ) : (
                      <>
                        <li>• SNS Launcherアプリがインストール済み</li>
                        <li>• SNS LauncherからSNSを開いてChromeで表示される</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>

          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/portal/dashboard')}
              className="w-full px-6 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-semibold transition-colors"
            >
              ダッシュボードに戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
